import { useState } from "react";
import { useAvailabilityTier } from "../../hooks/api/v2/availabilityTier/useAvailabilityTier";
import { useAvailabilityTierUpdate } from "../../hooks/api/v2/availabilityTier/useAvailabilityTierUpdate";
import { Alert } from "../../components/alert/Alert";
import { Button, InputLabel } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useProductAvailabilityTierToggleAll } from "../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierToggleAll";
import { BasicDialog } from "../../components/modal/BasicDialog";
import { AlertCompact } from "~/components/alert/AlertCompact";
import { DefaultProductCatalogueTooltip } from "./components/DefaultProductCatalogueTooltip";
import { AvailabilityTierNameForm } from "./components/AvailabilityTierNameForm";

export const AvailabilityTierDetail = ({
  availabilityTierId,
}: {
  availabilityTierId: number;
}) => {
  const [editedName, setEditedName] = useState<string | null>(null);
  const [confirmDialogType, setConfirmDialogType] = useState<
    "none" | "all" | null
  >(null);

  const { data: availabilityTier } = useAvailabilityTier(availabilityTierId);

  const availabilityTierUpdate = useAvailabilityTierUpdate(availabilityTierId);

  const productAvailabilityTierToggleAll =
    useProductAvailabilityTierToggleAll();

  const setAllTo = (on = true) => {
    productAvailabilityTierToggleAll.mutate({
      on,
      AvailabilityTierId: availabilityTierId,
    });
  };

  const name = editedName ?? availabilityTier?.name ?? "";
  const showNameSave =
    editedName !== null &&
    editedName !== availabilityTier?.name &&
    editedName.length > 0;

  const availabilityTierProcessing = availabilityTierUpdate.isPending;

  const isNbHiddenProductsLessThanNbProducts =
    (availabilityTier?.hiddenProducts ?? 0) > 0 &&
    (availabilityTier?.hiddenProducts ?? 0) <
      (availabilityTier?.totalNumberProducts ?? 0);

  return (
    <div className="flex flex-col gap-4 p-4">
      {availabilityTier?.defaultType && (
        <DefaultProductCatalogueTooltip hasMultipleDefaults={false}>
          <AlertCompact className="bg-zinc-100">
            <span>
              This is your <b>default product catalogue</b>
            </span>
          </AlertCompact>
        </DefaultProductCatalogueTooltip>
      )}
      <AvailabilityTierNameForm
        name={name}
        onNameChange={setEditedName}
        action="Update"
        showAction={showNameSave}
        loading={availabilityTierProcessing}
        onSubmit={() => {
          availabilityTierUpdate.mutate(
            { name },
            { onSuccess: () => setEditedName(null) },
          );
        }}
      />

      <Alert
        type="sellar"
        message={
          <>
            Suggested names could be <b>Full range, Core range,</b> or{" "}
            <b>Excluding returnables</b> for example
          </>
        }
        className="bg-zinc-50"
      />

      <div className="mx-auto flex w-full max-w-[920px] flex-col gap-2">
        <InputLabel>Toggle visibility for all product items</InputLabel>
        <div className="flex">
          <Button
            color="secondary"
            className={twMerge(
              "w-full rounded-r-none bg-white",
              availabilityTier?.hiddenProducts ===
                availabilityTier?.totalNumberProducts &&
                "pointer-events-none bg-zinc-100",
            )}
            onClick={() => {
              setConfirmDialogType("none");
            }}
            loading={productAvailabilityTierToggleAll.isPending}
          >
            None
          </Button>
          <Button
            color="secondary"
            className={twMerge(
              "w-full rounded-none border-x-0 bg-white",
              availabilityTier?.hiddenProducts === 0 &&
                "pointer-events-none bg-zinc-100",
            )}
            onClick={() => {
              setConfirmDialogType("all");
            }}
            loading={productAvailabilityTierToggleAll.isPending}
          >
            All
          </Button>
          <Button
            color="secondary"
            className={twMerge(
              "pointer-events-none w-full rounded-l-none bg-white",
              isNbHiddenProductsLessThanNbProducts && "bg-zinc-100",
            )}
            loading={productAvailabilityTierToggleAll.isPending}
          >
            Custom
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-zinc-100 p-4 italic">
        {availabilityTier?.hiddenProducts ?? 0} product items hidden
      </div>

      <BasicDialog
        dialogActive={confirmDialogType !== null}
        title="Confirm Visibility"
        description={
          confirmDialogType === "none"
            ? "Are you sure you want to turn off visibility for all products?"
            : "Are you sure you want all products to be visible?"
        }
        icon="ALERT_CIRCLE_OUTLINE"
        confirmButtonText="Confirm"
        deleteButtonText="Cancel"
        onConfirm={() => {
          if (confirmDialogType === "none") {
            setAllTo(false);
          } else if (confirmDialogType === "all") {
            setAllTo(true);
          }
          setConfirmDialogType(null);
        }}
        onDismiss={() => setConfirmDialogType(null)}
        onDelete={() => setConfirmDialogType(null)}
        loading={productAvailabilityTierToggleAll.isPending}
      />
    </div>
  );
};
